import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, KeyRound, Loader2, Mail, Power, TriangleAlert } from "lucide-react";
import { api } from "../../lib/api";
import { moveQuestion, releaseNote } from "../../lib/limoGroup";
import { accountLine, cameBack, passwordLine, reactivationChoices } from "../../lib/limoAccount";

/**
 * This participant at Logiscool: enrol them, see their access details, reset.
 *
 * Manual on purpose while licences are short. With thirty student licences
 * and more paid enrolments than that, automatic enrolment would fail silently
 * for whoever comes thirty-first; a button makes each spend a visible choice.
 *
 * Credentials are read live and held only in this component's state. They
 * never go into a toast — toasts are the one piece of UI people screenshot
 * without thinking — and they are dropped when the mode changes or the
 * drawer closes.
 */
const detail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

/** Why an access email did not leave, worded as what to do next. */
const ACCESS_REASONS = {
  credentials_unavailable: "Logiscool nu a returnat încă datele de acces. Încearcă din nou peste un minut.",
  password_changed_by_user: "Participantul și-a schimbat singur parola. Resetează parola — cea nouă se trimite automat.",
  email_failed: "Emailul nu a putut fi trimis. Verifică setările Resend.",
  no_username: "Logiscool nu a returnat utilizatorul.",
  not_enrolled: "Participantul nu e încă înscris în Logiscool.",
  error: "A apărut o eroare la trimiterea emailului.",
};

const accessNote = (email, sandbox) => {
  if (!email) return "";
  if (email.sent) {
    return sandbox ? " Datele de acces ți-au fost trimise ție, ca probă." : " Datele de acces au fost trimise pe email.";
  }
  return ` Emailul cu datele de acces nu a plecat: ${ACCESS_REASONS[email.reason] || "motiv necunoscut."}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO");
};

export default function LimoEnrollmentPanel({ enrollmentId, canMutate }) {
  const [sandbox, setSandbox] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  // Whether they can sign in, read from Logiscool when the drawer opens —
  // never the password (08.10.2026).
  const [account, setAccount] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  const load = useCallback(async (mode) => {
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/limo`, { params: { sandbox: mode } });
      setStatus(r.data);
    } catch (err) {
      setStatus({ error: detail(err, "Nu am putut citi starea Logiscool.") });
    }
  }, [enrollmentId]);

  useEffect(() => {
    setCreds(null);
    setAccount(null);
    setReactivating(false);
    load(sandbox);
    return () => setCreds(null);
  }, [load, sandbox]);

  const loadAccount = useCallback(async (mode) => {
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/limo/account`, { params: { sandbox: mode } });
      setAccount(r.data);
    } catch {
      setAccount({ ok: false });
    }
  }, [enrollmentId]);

  const enrolled = Boolean(status?.enrolled);
  useEffect(() => {
    if (enrolled) loadAccount(sandbox);
  }, [enrolled, loadAccount, sandbox]);

  const reactivate = async ({ readd }) => {
    const withdrawn = Boolean(account?.withdrawn);
    if (withdrawn && !window.confirm(reactivationChoices(account).withdrawnWarning)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/reactivate`, null,
        { params: { sandbox, readd, confirm_withdrawn: withdrawn } });
      (r.data.readd_error ? toast.warning : toast.success)(r.data.message);
      setReactivating(false);
      await load(sandbox);
      await loadAccount(sandbox);
    } catch (err) {
      toast.error(detail(err, "Reactivarea nu a reușit."));
    } finally {
      setBusy(false);
    }
  };

  const enroll = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/enroll`, null, { params: { sandbox } });
      if (r.data?.ok) {
        const base = r.data.already ? "Era deja înscris."
          : sandbox ? "Înscris de probă — nicio licență reală consumată." : "Înscris în Logiscool.";
        const email = r.data.access_email;
        // A failed email is not a failed enrolment — the licence is spent either
        // way — so it is a warning that says what is still missing.
        (email && !email.sent ? toast.warning : toast.success)(base + accessNote(email, sandbox));
        await load(sandbox);
      } else {
        toast.error(r.data?.detail || r.data?.error || "Înscrierea a eșuat.");
      }
    } catch (err) {
      toast.error(detail(err, "Înscrierea a eșuat."));
    } finally {
      setBusy(false);
    }
  };

  const showCredentials = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/limo/credentials`, { params: { sandbox } });
      if (r.data?.ok) setCreds({ ...r.data, fresh: false });
      else toast.error(r.data?.error === "student_not_found"
        ? "Logiscool nu găsește acest cursant." : r.data?.error || "Nu am putut citi datele de acces.");
    } catch (err) {
      toast.error(detail(err, "Nu am putut citi datele de acces."));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    // A new password does not open a disabled account: ask first, and let
    // the team decide on the reactivation (08.10.2026).
    if (account?.enabled === false && canMutate
      && window.confirm("Contul e dezactivat la Logiscool: cu o parolă nouă tot nu va putea intra. Îl reactivezi întâi?")) {
      setReactivating(true);
      return;
    }
    if (!window.confirm(sandbox
      ? "Generezi o parolă nouă? Cea veche nu va mai funcționa. Emailul cu noua parolă îți vine ție, ca probă."
      : "Generezi o parolă nouă? Cea veche nu va mai funcționa. Noua parolă se trimite automat pe email participantului.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/reset-password`, null, { params: { sandbox } });
      if (r.data?.ok) {
        setCreds({ username: r.data.username, password: r.data.password, fresh: true });
        const email = r.data.access_email;
        (email && !email.sent ? toast.warning : toast.success)("Parolă nouă generată." + accessNote(email, sandbox));
        await load(sandbox);
        await loadAccount(sandbox);
      } else {
        toast.error(r.data?.error || "Resetarea a eșuat.");
      }
    } catch (err) {
      toast.error(detail(err, "Resetarea a eșuat."));
    } finally {
      setBusy(false);
    }
  };

  const sendAccess = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/send-access`, null, { params: { sandbox } });
      if (r.data?.sent) {
        toast.success(sandbox ? "Trimis ție, ca probă." : "Datele de acces au fost trimise participantului.");
        await load(sandbox);
      } else {
        toast.error(ACCESS_REASONS[r.data?.reason] || "Emailul nu a putut fi trimis.");
      }
    } catch (err) {
      toast.error(detail(err, "Emailul nu a putut fi trimis."));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiat.");
    } catch {
      toast.error("Nu am putut copia.");
    }
  };

  const cap = status?.capacity;
  const release = releaseNote(status?.release, status?.released_at);

  const moveHere = async () => {
    if (!window.confirm(moveQuestion(status.move))) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/move`, null, { params: { sandbox } });
      if (r.data.partial) toast.warning(r.data.message); else toast.success(r.data.message);
      await load(sandbox);
    } catch (err) {
      toast.error(detail(err, "Mutarea nu a reușit."));
    } finally {
      setBusy(false);
    }
  };

  // A group on the course library needs no Logiscool account (28.10.2026,
  // decision 13). Somebody who already has one keeps the panel, to manage it.
  if (status && !status.error && status.content_format === "library" && !status.enrolled) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 text-sm text-[#6B7280]" data-testid="enr-limo-library">
        <div className="font-semibold text-[#373A45]">Logiscool</div>
        Grupa folosește biblioteca cursului: cursantul nu are nevoie de cont Logiscool.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="enr-limo-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-[#373A45]">Logiscool</div>
        {status?.enrolled && (
          <span className="inline-flex rounded-full bg-[#519872]/15 text-[#2F6B4E] px-2.5 py-1 text-[11px] font-semibold">
            {sandbox ? "Înscris (probă)" : "Înscris"}
          </span>
        )}
      </div>

      <label className="mt-2 flex items-center gap-2 text-xs text-[#373A45] cursor-pointer">
        <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)}
          className="h-4 w-4 accent-[#965EA7]" data-testid="enr-limo-sandbox" />
        <span>Mod de probă <span className="text-[#6B7280]">— pool-urile TEST, evidență separată</span></span>
      </label>

      {status === null && (
        <div className="mt-3 flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={14} className="animate-spin" /> Se verifică…
        </div>
      )}

      {status?.error && <div className="mt-3 text-xs text-[#9b1f2c]">{status.error}</div>}

      {release && (
        <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${release.tone === "done"
          ? "bg-[#EDEEF0] text-[#373A45]" : "bg-[#FDF3E7] text-[#8a6a1a]"}`} data-testid="enr-limo-release">
          {release.text}
        </div>
      )}

      {status?.move?.needed && !status?.released_at && (
        <div className="mt-3 rounded-lg bg-[#EDE3F2] px-3 py-2 text-xs text-[#6B3F7A]" data-testid="enr-limo-move">
          La Logiscool e încă în „{status.move.current_group_name || "grupa veche"}”, nu în „{status.move.target_group_name}”.
          {canMutate && (
            <button type="button" onClick={moveHere} disabled={busy}
              className="ml-2 rounded-full bg-white border border-[#6B3F7A]/30 px-3 py-0.5 font-semibold hover:bg-[#F5F0F8] disabled:opacity-60"
              data-testid="enr-limo-move-btn">
              Mută și în Logiscool
            </button>
          )}
        </div>
      )}
      {status?.move?.blocked && !status?.released_at && (
        <div className="mt-3 rounded-lg bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">{status.move.blocked}</div>
      )}

      {/* On every enrolment, not only the one that would fail: how many paid
          participants are still waiting against how many licences are left is
          worth knowing before choosing who to enrol next. */}
      {cap?.warning && (
        <div className="mt-3 flex gap-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#8a6a1a]"
          data-testid="enr-limo-capacity-warning">
          <TriangleAlert size={14} className="shrink-0 mt-0.5" />
          <span>
            <strong>{cap.pending}</strong> participanți plătiți așteaptă o licență, dar mai sunt doar{" "}
            <strong>{cap.free}</strong> libere{cap.pool_name ? ` în „${cap.pool_name}”` : ""}. Lipsesc{" "}
            <strong>{cap.shortfall}</strong>.
          </span>
        </div>
      )}

      {status && !status.error && !status.enrolled && (
        status.ready ? (
          canMutate && (
            <div className="mt-3">
              <button type="button" onClick={enroll} disabled={busy}
                className={`rounded-full text-white text-sm font-semibold px-4 py-1.5 disabled:opacity-60 ${
                  sandbox ? "bg-[#519872] hover:bg-[#3f7a5c]" : "bg-[#965EA7] hover:bg-[#7A4986]"}`}
                data-testid="enr-limo-enroll">
                {busy ? "Se înscrie…" : sandbox ? "Înscrie de probă" : "Înscrie în Logiscool"}
              </button>
              <div className="mt-1 text-[11px] text-[#6B7280]">
                {sandbox ? "Nu consumă licențe reale."
                  : `Consumă o licență de cursant${cap?.known ? ` — rămân ${Math.max(0, (cap.free || 0) - 1)} după.` : "."}`}
              </div>
            </div>
          )
        ) : (
          <ul className="mt-3 space-y-0.5 text-xs text-[#6B7280]" data-testid="enr-limo-problems">
            {status.problems.map((p) => <li key={p}>• {p}</li>)}
          </ul>
        )
      )}

      {status?.enrolled && (
        <AccountState account={account} status={status} canMutate={canMutate} busy={busy}
          reactivating={reactivating} setReactivating={setReactivating} onReactivate={reactivate} />
      )}

      {status?.enrolled && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={showCredentials} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
            data-testid="enr-limo-credentials">
            <Eye size={13} /> Vezi datele de acces
          </button>
          {canMutate && (
            <button type="button" onClick={resetPassword} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
              data-testid="enr-limo-reset">
              <KeyRound size={13} /> Resetează parola
            </button>
          )}
          {canMutate && (
            <button type="button" onClick={sendAccess} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
              data-testid="enr-limo-send-access">
              <Mail size={13} /> {status.access_sent_at ? "Retrimite datele de acces" : "Trimite datele de acces"}
            </button>
          )}
          {/* Whether the teacher has been told their login at all — the first
              thing somebody on the phone with them needs to know. */}
          <div className="basis-full text-[11px] text-[#6B7280]" data-testid="enr-limo-access-status">
            {status.access_sent_at
              ? `Datele de acces trimise pe email la ${fmtDate(status.access_sent_at)}${sandbox ? " (către tine, probă)" : ""}.`
              : "Datele de acces nu au fost trimise încă."}
          </div>
        </div>
      )}

      {creds && (
        <div className="mt-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] p-3 text-xs" data-testid="enr-limo-creds">
          <CredRow label="Utilizator" value={creds.username} onCopy={copy} />
          {creds.password ? (
            <CredRow label={creds.fresh ? "Parolă nouă" : "Parolă"} value={creds.password} onCopy={copy} mono />
          ) : (
            <div className="mt-1 text-[#8a6a1a]">
              Parola a fost schimbată de cursant în MyLogiscool, deci nu mai e cunoscută. Resetează-o dacă a uitat-o.
            </div>
          )}
          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-[#6B7280]">
            <span>Citită acum de la Logiscool. Nu se salvează nicăieri.</span>
            <button type="button" onClick={() => setCreds(null)} className="underline">Ascunde</button>
          </div>
        </div>
      )}
    </div>
  );
}

const TONES = {
  ok: "bg-[#EAF4EE] text-[#2F6B4E]",
  off: "bg-[#FDECEC] text-[#9b1f2c]",
  warn: "bg-[#FDF3E7] text-[#8a6a1a]",
  unknown: "bg-[#EDEEF0] text-[#373A45]",
};

/** Can they sign in, who disabled the account, and the way back. */
function AccountState({ account, status, canMutate, busy, reactivating, setReactivating, onReactivate }) {
  if (!account) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-[#6B7280]">
        <Loader2 size={12} className="animate-spin" /> Se citește starea contului…
      </div>
    );
  }
  const line = accountLine(account);
  const pwd = passwordLine(account);
  const choices = reactivationChoices(account, { groupName: status.group_name, free: status.capacity?.free });
  // Reactivated earlier without the group: the licence is still missing.
  const outOfGroup = Boolean(status.release?.reactivated_at && status.release?.removed_at);
  return (
    <div className="mt-3 space-y-2" data-testid="enr-limo-account">
      {line && (
        <div className={`rounded-lg px-3 py-2 text-xs font-semibold ${TONES[line.tone]}`} data-testid="enr-limo-account-state">
          {line.text}
          {pwd && <div className="mt-0.5 font-normal opacity-90">{pwd}</div>}
        </div>
      )}
      {cameBack(account) && (
        <div className="rounded-lg bg-[#EDE3F2] px-3 py-2 text-xs text-[#6B3F7A]" data-testid="enr-limo-came-back">
          Înscrierea e din nou plătită, dar contul Logiscool a rămas dezactivat de la retragere. Îl reactivezi doar dacă
          echipa decide asta.
        </div>
      )}
      {canMutate && account.enabled === false && !reactivating && (
        <button type="button" onClick={() => setReactivating(true)} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
          data-testid="enr-limo-reactivate">
          <Power size={13} /> Reactivează contul
        </button>
      )}
      {canMutate && outOfGroup && account.enabled !== false && (
        <button type="button" onClick={() => onReactivate({ readd: true })} disabled={busy || status.capacity?.free === 0}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7]/40 text-[#6B3F7A] hover:bg-[#F5F0F8] disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
          data-testid="enr-limo-readd">
          Adaugă la loc în „{status.group_name || "grupa lui"}” (consumă o licență)
        </button>
      )}
      {canMutate && reactivating && (
        <div className="rounded-lg border border-[#965EA7]/30 bg-[#F5F0F8] p-3 text-xs text-[#373A45]" data-testid="enr-limo-reactivate-box">
          <div className="font-semibold">Reactivezi contul Logiscool?</div>
          <div className="mt-0.5 text-[#6B7280]">Cursantul va putea intra din nou cu aceleași date; licențele lui se reiau.</div>
          {choices.withdrawnWarning && <div className="mt-1.5 font-semibold text-[#9b1f2c]">{choices.withdrawnWarning}</div>}
          <div className="mt-2 flex flex-wrap gap-2">
            {choices.readdLabel && (
              <button type="button" onClick={() => onReactivate({ readd: true })} disabled={busy || !choices.canReadd}
                className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white font-semibold px-3 py-1.5"
                data-testid="enr-limo-reactivate-readd">
                {choices.readdLabel}
              </button>
            )}
            <button type="button" onClick={() => onReactivate({ readd: false })} disabled={busy}
              className={`rounded-full font-semibold px-3 py-1.5 disabled:opacity-60 ${choices.readdLabel
                ? "border border-[#965EA7]/40 bg-white text-[#6B3F7A] hover:bg-[#F5F0F8]"
                : "bg-[#965EA7] hover:bg-[#7A4986] text-white"}`}
              data-testid="enr-limo-reactivate-only">
              {choices.readdLabel ? "Doar reactivează contul" : "Reactivează contul"}
            </button>
            <button type="button" onClick={() => setReactivating(false)} disabled={busy}
              className="rounded-full px-3 py-1.5 font-semibold text-[#6B7280] hover:text-[#373A45]">
              Renunță
            </button>
          </div>
          {choices.readdNote && <div className="mt-1.5 text-[11px] text-[#6B7280]">{choices.readdNote}</div>}
        </div>
      )}
    </div>
  );
}

function CredRow({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className={`text-[#373A45] ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
        {value && (
          <button type="button" onClick={() => onCopy(value)} className="text-[#6B7280] hover:text-[#373A45]" title="Copiază">
            <Copy size={12} />
          </button>
        )}
      </span>
    </div>
  );
}
